import * as React from "react";
import { CssBaseline, Container, Box } from "@mui/material";

import { LinkToCinemaSelectPage } from "../components/TextLinks/LinkToCinemaSelectPage.tsx";
import { LandingPageDescriptionBoxes } from "../components/LandingPageDescriptionBoxes/LandingPageDescriptionBoxes.tsx";
import { TitleElement } from "../components/TitleElement/TitleElement.tsx";
import { StandardPageHead } from "../components/Heads/StandardPageHead.tsx";

import { ChildVenue } from "../types/dataTypes.ts";
import { useFilmListingsController } from "../hooks/useFilmListingsController.ts";
import { FilmListingsSection } from "../components/FilmListingsSection/FilmListingsSection.tsx";
import { LoadingElementInBox } from "../components/Loading/LoadingElement.tsx";
import { TextLinkTemplate } from "../components/TextLinks/TextLinkTemplate.tsx";

type TodayCityLandingPageProps = {
  locationText: string;
  availableVenuesText: string;
  citySlug: string;
  latitude: number;
  longitude: number;
  nearby_venues: ChildVenue[];
  dateLabels: string[];
  dayText: string;
  dateSlug: string;
};

export const TodayCityLandingPage: React.FC<TodayCityLandingPageProps> = ({
  locationText,
  availableVenuesText,
  citySlug,
  nearby_venues,
  dateLabels,
  dayText,
  dateSlug,
}) => {
  const validVenueSlugs = React.useMemo(
    () => nearby_venues.map((v) => v.venue_slug),
    [nearby_venues],
  );

  const initialVenues = React.useMemo(
    () => nearby_venues.slice(0, 5).map((v) => v.venue_slug),
    [nearby_venues],
  );

  const controller = useFilmListingsController({
    contextSlug: `${citySlug}/${dateSlug}`,
    initialSelectedVenues: initialVenues,
    validVenueSlugs,
    defaultOptimise: false,
    enabled: validVenueSlugs.length > 0 && initialVenues.length > 0,
    selectedDateTabs: dateLabels,
  });

  return (
    <>
      <CssBaseline />
      <StandardPageHead
        title={`Tickets & Showtimes for every Film Showing in ${locationText} ${dayText}`}
        description={`Browse movie showtimes for every film showing ${dayText} in ${locationText} on one page.`}
        pageUrl={`https://cinemaguideireland.com/${citySlug}/${dateSlug}`}
      />

      {controller.isInitialLoading ? (
        <LoadingElementInBox />
      ) : (
        <>
          <Box
            component="main"
            sx={{
              bgcolor: "background.default",
              color: "text.primary",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <TitleElement
              headerOneText={`Plan your next cinema trip in ${locationText}.`}
              headerTwoText={`View every film showing in ${locationText} ${dayText.toLowerCase()} in one place.`}
            />

            <LandingPageDescriptionBoxes
              locationText={`${locationText}`}
              boxOneHeader={`${nearby_venues.length}+ ${locationText} Cinemas`}
              boxOneText={availableVenuesText}
              boxTwoHeader="Purchase Cinema Tickets"
              boxTwoText={`Buy tickets to watch any film showing in ${locationText} ${dayText.toLowerCase()}. Pay no extra fees. Book tickets fast.`}
              boxThreeHeader={`All Cinema Showtimes in ${locationText} ${dayText}`}
              boxThreeText={`See every film showing in ${locationText}. Compare different showtimes. Get the cheapest cinema tickets.`}
            />

            <Container sx={{ my: 3 }}>
              <LinkToCinemaSelectPage
                leadText={`Not in ${locationText}?`}
                appendHref={false}
                fontSizeParam={{ xs: "small", md: "x-large" }}
              />
              <TextLinkTemplate
                leadText={`Want to see screenings for all dates in ${locationText}?`}
                linkText="Click here"
                href={citySlug}
                appendHref={false}
                fontSizeParam={{ xs: "small", md: "x-large" }}
              />
            </Container>
          </Box>

          <Box sx={{ mx: "3%" }}>
            <FilmListingsSection
              controller={controller}
              venuesNearLocation={nearby_venues}
              selectAllVenuesLabel={`SELECT ALL VENUES NEAR ${locationText.toUpperCase()}`}
              venueDistanceLabel="from city centre"
              forOneDay={true}
              containerMt={{ xs: 0, md: 0 }}
            />
          </Box>
        </>
      )}

      <noscript>
        <div>
          CinemaGuide requires JavaScript to display the latest showtimes.
          Please enable JavaScript in your browser.
        </div>
      </noscript>
    </>
  );
};
